import axios from 'axios';
class ApiService {
    constructor() {
        Object.defineProperty(this, "api", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Em desenvolvimento, usar URLs relativas para aproveitar o proxy do Vite
        const isDevelopment = import.meta.env.DEV;
        const baseURL = isDevelopment ? '/api' : (import.meta.env.VITE_API_URL || 'http://localhost:4050/api');
        this.api = axios.create({
            baseURL,
            timeout: 60000,
            headers: {
                'Content-Type': 'application/json'
            }
        });
        this.setupInterceptors();
    }
    setupInterceptors() {
        // Request interceptor para adicionar token
        this.api.interceptors.request.use((config) => {
            const token = localStorage.getItem('token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        }, (error) => {
            return Promise.reject(error);
        });
        // Response interceptor para tratar erros
        this.api.interceptors.response.use((response) => {
            // DEBUG: Log todas as respostas de dashboard/metrics
            if (response.config.url?.includes('dashboard/metrics')) {
                console.log('🔍 [API INTERCEPTOR] Resposta /api/dashboard/metrics:');
                console.log('   Status:', response.status);
                console.log('   Headers:', response.headers);
                console.log('   Data completo:', JSON.stringify(response.data, null, 2));
                if (response.data?.data) {
                    console.log('   Faturamento mes:', response.data.data.faturamento?.mes);
                    console.log('   Clientes ativos:', response.data.data.clientes?.ativos);
                }
            }
            return response;
        }, (error) => {
            console.error('API Error:', error);
            // Token expirado ou inválido
            if (error.response?.status === 401) {
                this.handleUnauthorized();
            }
            // Erro de rede
            if (!error.response) {
                throw new Error('Erro de rede. Verifique sua conexão.');
            }
            // Retornar erro da API ou erro genérico
            const apiError = error.response?.data?.error || 'Erro interno do servidor';
            throw new Error(apiError);
        });
    }
    handleUnauthorized() {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
    }
    // Métodos HTTP genéricos
    async get(url, config) {
        const response = await this.api.get(url, config);
        return response.data;
    }
    async post(url, data, config) {
        const response = await this.api.post(url, data, config);
        return response.data;
    }
    async put(url, data, config) {
        const response = await this.api.put(url, data, config);
        return response.data;
    }
    async delete(url, config) {
        const response = await this.api.delete(url, config);
        return response.data;
    }
    async patch(url, data, config) {
        const response = await this.api.patch(url, data, config);
        return response.data;
    }
    // Upload de arquivos
    async upload(url, formData) {
        const response = await this.api.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    }
    // Download de arquivos
    async download(url, filename) {
        const response = await this.api.get(url, {
            responseType: 'blob'
        });
        const blob = new Blob([response.data]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }
    // Gerar e baixar PDF
    async generatePdf(vendaId, tipo, options) {
        const response = await this.api.post('/pdf/generate', {
            vendaId,
            tipo,
            options
        }, {
            responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        // Extrair nome do arquivo do header Content-Disposition se disponível
        const contentDisposition = response.headers['content-disposition'];
        let filename = `${tipo}_${vendaId}_${new Date().toISOString().slice(0, 10)}.pdf`;
        if (contentDisposition) {
            const matches = contentDisposition.match(/filename="(.+)"/);
            if (matches && matches[1]) {
                filename = matches[1];
            }
        }
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }
    // Gerar e baixar PDF de ordem de serviço (cupom térmico)
    async generateOrdemServicoPdf(ordemServicoId, tipo, options) {
        const token = localStorage.getItem('token');
        // Usar os novos endpoints de cupom térmico PDF
        const endpoint = tipo === 'ordem'
            ? `/ordens-servico/${ordemServicoId}/cupom/abertura`
            : `/ordens-servico/${ordemServicoId}/pdf/fechamento`;
        const response = await this.api.get(endpoint, {
            params: { token },
            responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        // Extrair nome do arquivo do header Content-Disposition se disponível
        const contentDisposition = response.headers['content-disposition'];
        let filename = `${tipo === 'ordem' ? 'cupom-abertura-os' : 'fechamento-os'}_${ordemServicoId}_${new Date().toISOString().slice(0, 10)}.pdf`;
        if (contentDisposition) {
            const matches = contentDisposition.match(/filename="(.+)"/);
            if (matches && matches[1]) {
                filename = matches[1];
            }
        }
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }
    // Gerar e baixar PDF de orçamento (cupom térmico)
    async generateOrcamentoPdf(orcamentoId, options) {
        const token = localStorage.getItem('token');
        const response = await this.api.get(`/orcamentos/${orcamentoId}/cupom/orcamento`, {
            params: { token },
            responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        // Extrair nome do arquivo do header Content-Disposition se disponível
        const contentDisposition = response.headers['content-disposition'];
        let filename = `cupom-orcamento_${orcamentoId}_${new Date().toISOString().slice(0, 10)}.pdf`;
        if (contentDisposition) {
            const matches = contentDisposition.match(/filename="(.+)"/);
            if (matches && matches[1]) {
                filename = matches[1];
            }
        }
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }
    // Verificar saúde da API
    async healthCheck() {
        try {
            await this.get('/health');
            return true;
        }
        catch {
            return false;
        }
    }
    // Obter instância do axios para casos especiais
    getAxiosInstance() {
        return this.api;
    }
}
// Instância singleton
const apiService = new ApiService();
export default apiService;
// Utilitários para trabalhar com URLs
export const buildQueryString = (params) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
        }
    });
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
};
// Utilitário para formatação de erros
export const formatApiError = (error) => {
    if (error.response?.data?.error) {
        return error.response.data.error;
    }
    if (error.message) {
        return error.message;
    }
    return 'Erro desconhecido';
};
